import React, { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Live mesh-gradient stage.
 *
 * Renders a fixed-size set of blurred radial colour orbs as DIVs and animates
 * their positions via `transform: translate3d(...)` — GPU-composited, so the
 * scrolling page never repaints because of the moving gradient.
 *
 * Two simultaneous behaviours:
 *   1. Ambient drift  — each orb wanders along its own slow phase.
 *   2. Scroll gust    — scrolling injects a soft eased offset that decays.
 *
 * Honors prefers-reduced-motion (skips JS animation; static positions remain).
 *
 * Usage:
 *   <div className="relative bg-glass-gradient overflow-hidden">
 *     <LiveGradient fixed />        // viewport-locked, doesn't scroll with page
 *     ...page content...
 *   </div>
 */

const ORBS = [
  { color: "rgba(245,199,104,0.85)", size: 75, home: [22, 22], ampX: 8,  ampY: 6,  freqX: 0.00045, freqY: 0.00060, phaseX: 0.0, phaseY: 1.2 },
  { color: "rgba(241,168,201,0.85)", size: 70, home: [76, 30], ampX: 7,  ampY: 8,  freqX: 0.00055, freqY: 0.00040, phaseX: 1.7, phaseY: 0.4 },
  { color: "rgba(200,176,245,0.85)", size: 78, home: [30, 76], ampX: 9,  ampY: 6,  freqX: 0.00038, freqY: 0.00058, phaseX: 3.1, phaseY: 2.0 },
  { color: "rgba(190,234,240,0.85)", size: 70, home: [80, 78], ampX: 7,  ampY: 8,  freqX: 0.00050, freqY: 0.00038, phaseX: 4.5, phaseY: 3.3 },
  { color: "rgba(255,230,210,0.55)", size: 55, home: [50, 50], ampX: 10, ampY: 8,  freqX: 0.00030, freqY: 0.00048, phaseX: 2.2, phaseY: 5.0 },
  // Brighter highlight orb — the "breath" pass.
  { color: "rgba(255,255,255,0.55)", size: 35, home: [50, 50], ampX: 22, ampY: 16, freqX: 0.00026, freqY: 0.00020, phaseX: 0.7, phaseY: 2.9, highlight: true },
];

export const LiveGradient = ({ fixed = false }) => {
  const stageRef = useRef(null);
  const orbRefs = useRef([]);

  // Set initial transforms synchronously so the first paint already shows orbs
  // in their home positions (no flash of empty pastel base).
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const { width, height } = stage.getBoundingClientRect();
    if (!width || !height) return;
    orbRefs.current.forEach((el, i) => {
      if (!el) return;
      const o = ORBS[i];
      const px = (o.home[0] / 100) * width - el.offsetWidth / 2;
      const py = (o.home[1] / 100) * height - el.offsetHeight / 2;
      el.style.transform = `translate3d(${px}px, ${py}px, 0)`;
    });
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    let raf = 0;
    const startTs = performance.now();
    let lastScrollY = window.scrollY || 0;

    let stageW = stage.clientWidth || window.innerWidth;
    let stageH = stage.clientHeight || window.innerHeight;
    const measure = () => {
      stageW = stage.clientWidth || window.innerWidth;
      stageH = stage.clientHeight || window.innerHeight;
    };
    measure();

    let gustX = 0;
    let gustY = 0;
    let targetGustX = 0;
    let targetGustY = 0;

    const GUST_GAIN = 0.022;
    const GUST_MAX = 14;
    const GUST_DECAY = 0.9;
    const GUST_EASE = 0.1;

    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - lastScrollY;
      lastScrollY = y;
      targetGustY = Math.max(-GUST_MAX, Math.min(GUST_MAX, targetGustY - dy * GUST_GAIN));
      targetGustX = Math.max(-GUST_MAX, Math.min(GUST_MAX, targetGustX + dy * GUST_GAIN * 0.35));
    };

    const onResize = () => measure();

    const tick = (ts) => {
      const t = ts - startTs;

      targetGustX *= GUST_DECAY;
      targetGustY *= GUST_DECAY;
      gustX += (targetGustX - gustX) * GUST_EASE;
      gustY += (targetGustY - gustY) * GUST_EASE;
      if (Math.abs(gustX) < 0.005) gustX = 0;
      if (Math.abs(gustY) < 0.005) gustY = 0;

      for (let i = 0; i < ORBS.length; i += 1) {
        const el = orbRefs.current[i];
        if (!el) continue;
        const o = ORBS[i];
        const sign = i % 2 === 0 ? 1 : -0.7;
        const xPct = o.home[0] + Math.sin(t * o.freqX + o.phaseX) * o.ampX + gustX * sign;
        const yPct = o.home[1] + Math.cos(t * o.freqY + o.phaseY) * o.ampY + gustY * sign;
        const px = (xPct / 100) * stageW - el.offsetWidth / 2;
        const py = (yPct / 100) * stageH - el.offsetHeight / 2;
        el.style.transform = `translate3d(${px.toFixed(2)}px, ${py.toFixed(2)}px, 0)`;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className={`live-gradient-stage${fixed ? " is-fixed" : ""}`}
      style={fixed ? { position: "fixed", inset: 0 } : undefined}
      aria-hidden="true"
    >
      {ORBS.map((o, i) => (
        <div
          key={i}
          ref={(el) => (orbRefs.current[i] = el)}
          className="live-gradient-orb"
          style={{
            "--c": o.color,
            width: `${o.size}vmax`,
            height: `${o.size}vmax`,
          }}
        />
      ))}
    </div>
  );
};

export default LiveGradient;
